import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * Variablerne, der kan sættes ind i en tekst: klik for at sætte ind, hvor markøren står, eller træk ned i
 * teksten. Bruges både til mails og SMS'er, så de to steder opfører sig ens.
 */

export interface Variable {
  key: string;
  description?: string;
  required?: boolean;
}

/** Husker det felt, markøren sidst var i, så en variabel lander der, hvor der blev skrevet */
export const useFieldFocus = () => {
  const field = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const remember = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    field.current = event.target;
  };
  const insert = (text: string) => {
    const target = field.current;
    if (!target) return;
    const at = target.selectionStart ?? target.value.length;
    // React lytter ikke på en almindelig tildeling, så værdien sættes gennem prototypen og en input-hændelse
    const setter = Object.getOwnPropertyDescriptor(
      target instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype,
      "value"
    )?.set;
    setter?.call(target, `${target.value.slice(0, at)}${text}${target.value.slice(target.selectionEnd ?? at)}`);
    target.dispatchEvent(new Event("input", { bubbles: true }));
    target.focus();
    target.setSelectionRange(at + text.length, at + text.length);
  };
  return { fieldProps: { onFocus: remember, onBlur: remember }, insert };
};

interface Props {
  variables: Variable[];
  onInsert: (variable: string) => void;
  /** Teksten der arbejdes i, så det kan ses, hvilke variabler der allerede er brugt */
  usedIn?: string;
  title?: string;
}

export default function VariablePalette({ variables, onInsert, usedIn = "", title }: Props) {
  const [t] = useTranslation("global");
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {title ?? t("mails.variables")}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {variables.map((variable) => {
          const token = `|*${variable.key}*|`;
          const used = usedIn.includes(token);
          return (
            <Tooltip key={variable.key} title={variable.description || ""} placement="right">
              <Chip
                label={token}
                size="small"
                draggable
                onDragStart={(event) => event.dataTransfer.setData("text/plain", token)}
                onClick={() => onInsert(token)}
                color={variable.required && !used ? "warning" : "default"}
                variant={used ? "filled" : "outlined"}
                sx={{ cursor: "grab" }}
              />
            </Tooltip>
          );
        })}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {t("mails.variableHelp")}
      </Typography>
    </Box>
  );
}
